import axios from "axios";
import { Capacitor } from "@capacitor/core";

// Safely detect if running as native mobile app
let isNativeApp = false;
try {
  isNativeApp = Capacitor.isNativePlatform();
} catch (e) {
  // Capacitor not available, running as web app
  isNativeApp = false;
}

// Production backend URL - skybday.app configured
const PRODUCTION_BACKEND_URL = "https://skybday.app";

// Use production URL for mobile app, otherwise use env variable or relative URL
const API_URL = isNativeApp 
  ? PRODUCTION_BACKEND_URL 
  : (process.env.REACT_APP_BACKEND_URL || "");

const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    "Content-Type": "application/json",
  },
  timeout: 15000, // 15s timeout pour éviter les blocages
});

// Simple memory cache for faster navigation
const memoryCache = new Map();
const CACHE_TTL = 60000; // 1 minute

export const getCached = (key) => {
  const item = memoryCache.get(key);
  if (item && Date.now() - item.timestamp < CACHE_TTL) {
    return item.data;
  }
  return null;
};

export const setCache = (key, data) => {
  memoryCache.set(key, { data, timestamp: Date.now() });
};

export const clearCache = (keyPattern) => {
  if (keyPattern) {
    for (const key of memoryCache.keys()) {
      if (key.includes(keyPattern)) memoryCache.delete(key);
    }
  } else {
    memoryCache.clear();
  }
};

// Date formatting utility - converts YYYY-MM-DD to DD/MM/YYYY
export const formatBirthday = (dateStr) => {
  if (!dateStr) return "";
  const parts = dateStr.split("-");
  if (parts.length !== 3) return dateStr;
  return `${parts[2]}/${parts[1]}/${parts[0]}`;
};

// Token cache for native and web app
let cachedToken = null;

// Ensure token is set for API calls (call once before batch operations)
export const ensureAuthToken = async (retries = 3) => {
  console.log('[API] ensureAuthToken called, cachedToken exists:', !!cachedToken, 'isNative:', isNativeApp);
  
  // CRITICAL FIX: Always check cachedToken FIRST before attempting storage reads
  // This fixes the race condition where token is set via setAuthToken() but not yet written to storage
  if (cachedToken) {
    api.defaults.headers.common['Authorization'] = `Bearer ${cachedToken}`;
    console.log('[API] Token ensured from memory cache (immediate)');
    return true;
  }
  
  if (isNativeApp) {
    for (let attempt = 0; attempt < retries; attempt++) {
      try {
        const { Preferences } = await import("@capacitor/preferences");
        const { value: token } = await Preferences.get({ key: "skybday_token" });
        console.log(`[API] Capacitor storage read (attempt ${attempt + 1}):`, token ? 'token found' : 'no token');
        if (token) {
          cachedToken = token;
          api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          console.log(`[API] Token ensured from Capacitor storage (attempt ${attempt + 1})`);
          return true;
        } else {
          console.warn(`[API] No token found in Capacitor storage (attempt ${attempt + 1})`);
          if (attempt < retries - 1) {
            // Wait before retrying - increase delay with each attempt
            await new Promise(resolve => setTimeout(resolve, 500 * (attempt + 1)));
          }
        }
      } catch (e) {
        console.error(`[API] Error ensuring token (attempt ${attempt + 1}):`, e);
        if (attempt < retries - 1) {
          await new Promise(resolve => setTimeout(resolve, 500 * (attempt + 1)));
        }
      }
    }
    console.error('[API] ensureAuthToken FAILED after all retries');
    return false;
  }
  // For web, ensure token from localStorage
  const token = localStorage.getItem("skybday_token");
  if (token) {
    cachedToken = token;
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    console.log('[API] Token ensured from localStorage');
    return true;
  }
  console.error('[API] ensureAuthToken FAILED - no token in localStorage');
  return false;
};

// Get token from storage (sync for web, uses cache for native)
const getToken = () => {
  // If we have a cached token, use it
  if (cachedToken) {
    console.log('[API] getToken: returning cached token');
    return cachedToken;
  }
  // For web, try to get from localStorage
  if (!isNativeApp) {
    try {
      const token = localStorage.getItem("skybday_token");
      if (token) {
        cachedToken = token; // Cache it
        console.log('[API] getToken: found token in localStorage');
        return token;
      }
    } catch (e) {
      // Ignore storage errors
    }
  }
  console.warn('[API] getToken: NO TOKEN AVAILABLE, isNative:', isNativeApp);
  return null;
};

// Set token (call this after login)
export const setAuthToken = (token) => {
  cachedToken = token;
  if (token) {
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    console.log('[API] Token set successfully');
  } else {
    delete api.defaults.headers.common['Authorization'];
    console.log('[API] Token cleared');
  }
};

// Add auth token to requests - SINGLE interceptor
api.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  } else {
    console.error('[API] REQUEST WITHOUT TOKEN:', config.url);
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      console.error('[API] 401 Unauthorized for:', error.config?.url);
      
      // Don't clear token or redirect during batch operations or background tasks
      // The calling code should handle 401 errors appropriately
      const isContactsEndpoint = error.config?.url?.includes('/contacts');
      const isMessagePacksEndpoint = error.config?.url?.includes('/message-packs');
      const isDeviceEndpoint = error.config?.url?.includes('/devices');
      const isPushEndpoint = error.config?.url?.includes('/push');
      const isSubscriptionEndpoint = error.config?.url?.includes('/subscription');
      
      // Only clear token and redirect for critical auth failures
      // Skip for background operations that shouldn't interrupt user flow
      const shouldSkipTokenClear = isContactsEndpoint || isMessagePacksEndpoint || 
                                    isDeviceEndpoint || isPushEndpoint || isSubscriptionEndpoint;
      
      if (!shouldSkipTokenClear) {
        console.warn('[API] Clearing token due to 401 on:', error.config?.url);
        try {
          if (!isNativeApp) {
            localStorage.removeItem("skybday_token");
            localStorage.removeItem("skybday_user");
          }
          cachedToken = null;
        } catch (e) {
          // Ignore storage errors
        }
        // Only redirect if not already on auth page
        if (!window.location.pathname.includes('/auth')) {
          window.location.href = "/auth";
        }
      } else {
        console.log('[API] Skipping token clear for background endpoint:', error.config?.url);
      }
    }
    return Promise.reject(error);
  }
);

// Widget data refresh helper (imported dynamically to avoid circular deps)
const refreshWidgetData = async () => {
  try {
    if (isNativeApp) {
      const { updateWidgetData } = await import('./widgetData');
      const response = await api.get("/dashboard/upcoming");
      if (response.data?.upcoming) {
        await updateWidgetData(response.data.upcoming);
      }
    }
  } catch (e) {
    console.log("Widget refresh skipped:", e.message);
  }
};

// Auth
export const authApi = {
  register: (data) => api.post("/auth/register", data),
  login: (data) => api.post("/auth/login", data),
  verifyEmail: (token) => api.post("/auth/verify-email", { token }),
  resendVerification: (email) => api.post("/auth/resend-verification", { email }),
  forgotPassword: (email) => api.post("/auth/forgot-password", { email }),
  resetPassword: (data) => api.post("/auth/reset-password", data),
  getMe: () => api.get("/auth/me"),
};

// Contacts with caching
export const contactsApi = {
  getAll: async () => {
    // Check memory cache first
    const cached = getCached('contacts-all');
    if (cached) {
      return { data: cached };
    }
    const response = await api.get("/contacts");
    setCache('contacts-all', response.data);
    return response;
  },
  getOne: async (id) => {
    const cached = getCached(`contact-${id}`);
    if (cached) {
      return { data: cached };
    }
    const response = await api.get(`/contacts/${id}`);
    setCache(`contact-${id}`, response.data);
    return response;
  },
  create: async (data, skipWidgetRefresh = false) => {
    // CRITICAL: On iOS native, ALWAYS ensure token is set before making request
    if (isNativeApp && !cachedToken) {
      console.log('[contactsApi.create] No cached token, fetching from Capacitor...');
      try {
        const { Preferences } = await import("@capacitor/preferences");
        const { value: token } = await Preferences.get({ key: "skybday_token" });
        if (token) {
          cachedToken = token;
          api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          console.log('[contactsApi.create] Token retrieved from Capacitor storage');
        } else {
          console.error('[contactsApi.create] NO TOKEN IN CAPACITOR STORAGE!');
          throw new Error("Authentication token not found");
        }
      } catch (e) {
        console.error('[contactsApi.create] Error getting token:', e);
        throw e;
      }
    }
    
    console.log('[contactsApi.create] Creating contact:', data.name, 'cachedToken exists:', !!cachedToken);
    try {
      const response = await api.post("/contacts", data);
      console.log('[contactsApi.create] Success for:', data.name);
      clearCache('contacts'); // Invalidate contacts cache
      if (!skipWidgetRefresh) {
        refreshWidgetData(); // Update widget after adding contact
      }
      return response;
    } catch (error) {
      console.error('[contactsApi.create] FAILED for:', data.name, 'Error:', error.response?.status, error.message);
      throw error;
    }
  },
  // Bulk import contacts (single API call for many contacts)
  bulkImport: async (contacts) => {
    console.log('[contactsApi.bulkImport] Importing', contacts.length, 'contacts');
    try {
      const response = await api.post("/contacts/bulk", { contacts });
      console.log('[contactsApi.bulkImport] Result:', response.data);
      clearCache('contacts'); // Invalidate contacts cache
      refreshWidgetData(); // Update widget after bulk import
      return response;
    } catch (error) {
      console.error('[contactsApi.bulkImport] FAILED:', error.response?.status, error.message);
      throw error;
    }
  },
  update: async (id, data) => {
    const response = await api.put(`/contacts/${id}`, data);
    refreshWidgetData(); // Update widget after editing contact
    return response;
  },
  delete: async (id) => {
    const response = await api.delete(`/contacts/${id}`);
    refreshWidgetData(); // Update widget after deleting contact
    return response;
  },
  deleteAll: async () => {
    const response = await api.delete("/contacts");
    refreshWidgetData(); // Update widget after bulk delete
    return response;
  },
  importFile: async (file) => {
    // For native apps, ensure token is retrieved from async storage first
    if (isNativeApp) {
      try {
        const { Preferences } = await import("@capacitor/preferences");
        const { value: token } = await Preferences.get({ key: "skybday_token" });
        if (token) {
          cachedToken = token;
          api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          console.log('[contactsApi.importFile] Token set from Capacitor storage');
        }
      } catch (e) {
        console.error('[contactsApi.importFile] Error getting token:', e);
      }
    }
    const formData = new FormData();
    formData.append("file", file);
    const response = await api.post("/contacts/import", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    refreshWidgetData(); // Update widget after import
    return response;
  },
  // Legacy CSV import (backward compatibility)
  importCSV: (file) => {
    const formData = new FormData();
    formData.append("file", file);
    return api.post("/contacts/import", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
  // Alias for importFile (used by OnboardingFlow)
  import: async (formData) => {
    // For native apps, ensure token is retrieved from async storage first
    if (isNativeApp) {
      try {
        const { Preferences } = await import("@capacitor/preferences");
        const { value: token } = await Preferences.get({ key: "skybday_token" });
        if (token) {
          cachedToken = token;
          api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          console.log('[contactsApi.import] Token set from Capacitor storage');
        }
      } catch (e) {
        console.error('[contactsApi.import] Error getting token:', e);
      }
    }
    const response = await api.post("/contacts/import", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    refreshWidgetData();
    return response;
  },
};

// Templates
export const templatesApi = {
  getAll: () => api.get("/templates"),
  create: (data) => api.post("/templates", data),
  update: (id, data) => api.put(`/templates/${id}`, data),
  delete: (id) => api.delete(`/templates/${id}`),
};

// Messages
export const messagesApi = {
  generate: (data) => api.post("/messages/generate", data),
  send: (data) => api.post("/messages/send", data),
};

// Gift Suggestions
export const giftsApi = {
  getSuggestions: (data) => api.post("/gifts/suggestions", data),
};

// Birthday Cards with caching
export const cardsApi = {
  // Legacy methods
  getTemplates: () => api.get("/cards/templates"),
  generate: (data) => api.post("/cards/generate", data),
  getHistory: () => api.get("/cards/history"),
  // New methods for digital cards with cache
  create: async (data) => {
    const response = await api.post("/cards", data);
    clearCache('cards'); // Invalidate cards cache
    return response;
  },
  getAll: async () => {
    const cached = getCached('cards-all');
    if (cached) return { data: cached };
    const response = await api.get("/cards");
    setCache('cards-all', response.data);
    return response;
  },
  view: (shareCode) => api.get(`/cards/view/${shareCode}`),
  delete: async (cardId) => {
    const response = await api.delete(`/cards/${cardId}`);
    clearCache('cards'); // Invalidate cards cache
    return response;
  },
  uploadImage: (imageData) => api.post("/cards/upload-image", { image_data: imageData }),
  getFreemiumStatus: async () => {
    const cached = getCached('freemium-status');
    if (cached) return { data: cached };
    const response = await api.get("/cards/freemium-status");
    setCache('freemium-status', response.data);
    return response;
  },
  // AI message generation (Premium only)
  generateMessage: (contactName, tone) => api.post("/cards/generate-message", { contact_name: contactName, tone }),
};

// Payments / Premium
export const paymentsApi = {
  getPlan: () => api.get("/payments/plan"),
  createCheckout: (originUrl) => api.post("/payments/checkout", { origin_url: originUrl }),
  getStatus: (sessionId) => api.get(`/payments/status/${sessionId}`),
};

// Push Notifications
export const notificationsApi = {
  getVapidKey: () => api.get("/notifications/vapid-public-key"),
  subscribe: (subscription) => api.post("/notifications/subscribe", subscription),
  unsubscribe: (subscription) => api.delete("/notifications/unsubscribe", { data: subscription }),
  getStatus: () => api.get("/notifications/status"),
  getDeviceStatus: () => api.get("/notifications/device-status"),
  updateSettings: (settings) => api.put("/notifications/settings", settings),
  sendTest: () => api.post("/notifications/test"),
  registerDevice: (data) => api.post("/notifications/register-device", data),
};

// Admin
export const adminApi = {
  checkAdmin: () => api.get("/admin/check"),
  getStats: () => api.get("/admin/stats"),
  getUsers: (skip = 0, limit = 50, search = "") => 
    api.get(`/admin/users?skip=${skip}&limit=${limit}${search ? `&search=${search}` : ""}`),
  getUserDetails: (userId) => api.get(`/admin/users/${userId}`),
  updateUser: (userId, updates) => api.put(`/admin/users/${userId}`, updates),
  deleteUser: (userId) => api.delete(`/admin/users/${userId}`),
  verifyUserEmail: (userId) => api.post(`/admin/users/${userId}/verify`),
  resetUserPassword: (userId, newPassword) => 
    api.post(`/admin/users/${userId}/reset-password?new_password=${encodeURIComponent(newPassword)}`),
  broadcast: (title, body) => api.post(`/admin/broadcast?title=${encodeURIComponent(title)}&body=${encodeURIComponent(body)}`),
  getAllContacts: (skip = 0, limit = 100) => api.get(`/admin/contacts?skip=${skip}&limit=${limit}`),
  getUpcomingBirthdays: (days = 7) => api.get(`/admin/upcoming-birthdays?days=${days}`),
};

// Scheduled Messages
export const scheduledMessagesApi = {
  getAll: () => api.get("/scheduled-messages"),
  getByContact: (contactId) => api.get(`/scheduled-messages/contact/${contactId}`),
  create: (data) => api.post("/scheduled-messages", data),
  delete: (id) => api.delete(`/scheduled-messages/${id}`),
};

// Dashboard
export const dashboardApi = {
  getUpcoming: () => api.get("/dashboard/upcoming"),
  getStats: () => api.get("/dashboard/stats"),
  getMilestones: () => api.get("/dashboard/milestones"),
};

// Settings
export const settingsApi = {
  getReminders: () => api.get("/settings/reminders"),
  updateReminders: (data) => api.put("/settings/reminders", data),
};

// Event Types
export const eventTypesApi = {
  getAll: () => api.get("/event-types"),
  getSuggestions: () => api.get("/event-types/suggestions"),
  create: (data) => api.post("/event-types", data),
  update: (id, data) => api.put(`/event-types/${id}`, data),
  delete: (id) => api.delete(`/event-types/${id}`),
};

// Events
export const eventsApi = {
  getAll: () => api.get("/events"),
  getByContact: (contactId) => api.get(`/events/contact/${contactId}`),
  getUpcoming: (days = 30) => api.get(`/events/upcoming?days=${days}`),
  create: (data) => api.post("/events", data),
  update: (id, data) => api.put(`/events/${id}`, data),
  delete: (id) => api.delete(`/events/${id}`),
};


// Badges
export const badgesApi = {
  getInfo: () => api.get("/badges"),
};

// Gamification API
export const gamificationApi = {
  getStats: () => api.get("/gamification/stats"),
};

// RGPD / GDPR API
export const gdprApi = {
  exportData: () => api.get("/user/export-data"),
  deleteAccount: (password) => api.delete("/user/delete-account", { params: { password } }),
};

// Users API
export const usersApi = {
  updateAvatar: (data) => api.put("/users/avatar", data),
  getMe: () => api.get("/users/me"),
  changePassword: (data) => api.put("/users/password", data),
};

// Message Packs API
export const messagePacksApi = {
  getAll: () => api.get("/message-packs"),
  getDetails: (packId) => api.get(`/message-packs/${packId}`),
  purchase: (packId, transactionId) => api.post("/message-packs/purchase", { pack_id: packId, transaction_id: transactionId }),
  getPurchasedMessages: () => api.get("/message-packs/purchased/messages"),
  // Special method for iOS that ensures token is set before request
  getPackMessages: async (packId) => {
    // For native apps, ensure token is retrieved from async storage first
    if (isNativeApp) {
      try {
        const { Preferences } = await import("@capacitor/preferences");
        const { value: token } = await Preferences.get({ key: "skybday_token" });
        if (token) {
          cachedToken = token;
          api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          console.log('[getPackMessages] Token set from Capacitor storage');
        } else {
          console.warn('[getPackMessages] No token found in Capacitor storage');
        }
      } catch (e) {
        console.error('[getPackMessages] Error getting token:', e);
      }
    }
    return api.get(`/message-packs/${packId}/messages`);
  },
};

// SMS API
export const smsApi = {
  getCredits: () => api.get("/sms/credits"),
  addCredits: (data) => api.post("/sms/add-credits", data),
  getScheduled: () => api.get("/sms/scheduled"),
  schedule: (data) => api.post("/sms/schedule", data),
  cancel: (smsId) => api.delete(`/sms/scheduled/${smsId}`),
};

export const stripeApi = {
  getConfig: () => api.get("/stripe/config"),
  createCheckout: (productId) => api.post("/stripe/checkout", { 
    product_id: productId, 
    origin_url: window.location.origin 
  }),
  getCheckoutStatus: (sessionId) => api.get(`/stripe/checkout/status/${sessionId}`),
};

export default api;
